import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldTextarea, FieldCustom,
} from './detail/DetailShell';
import { MultiChipSelect } from './campaign/MultiChipSelect';
import { RELATION_CONFIGS, relationLabel } from './relationConfigs';
import { usePageActions } from '../contexts/PermissionContext';

// Mobil Yetki Profili detay/düzenleme — standart tam-sayfa DetailShell kalıbı.
// Liste: /mobile-permissions (MobilePermissionsPage). Çözüm: profil → temsilci grubu.
const API = '/api/permission-profiles';
// Controller route [Route("api/[controller]")] → "api/RepresentativeGroups" (tireli değil!).
const GROUPS_API = '/api/RepresentativeGroups';

const ACTIVE_OPTIONS = [{ value: 'true', label: 'Aktif' }, { value: 'false', label: 'Pasif' }];
const ACTION_DEFS = [
  { key: 'view', label: 'Görüntüle', field: 'canView' },
  { key: 'add', label: 'Ekle', field: 'canAdd' },
  { key: 'edit', label: 'Düzenle', field: 'canEdit' },
  { key: 'delete', label: 'Sil', field: 'canDelete' },
  { key: 'export', label: 'Dışa Aktar', field: 'canExport' },
];

const EMPTY_FORM = { name: '', description: '', isActive: 'true' };

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(p) { return pick(p, 'Data', 'data') ?? p; }

// İzin satırı yoksa "açık" = ekranın desteklediği tüm aksiyonlar aktif (geriye dönük uyum).
function openRight(actions) {
  const r = { canView: false, canAdd: false, canEdit: false, canDelete: false, canExport: false };
  ACTION_DEFS.forEach((a) => { if (actions.includes(a.key)) r[a.field] = true; });
  return r;
}

export function MobilePermissionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete, canCopy } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Profil' : 'Profil Detayı', 'Mobil ekran/aksiyon yetki profili.');

  const [tab, setTab] = useState('info');
  const [form, setForm] = useState(EMPTY_FORM);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const [catalog, setCatalog] = useState([]);
  const [rights, setRights] = useState({});          // code -> right
  const [groupChips, setGroupChips] = useState([]);  // [{ value, label }] — çoklu seçim chip'leri
  const [expanded, setExpanded] = useState(() => new Set());
  const [search, setSearch] = useState('');

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  const codeOf = (sc) => sc.code ?? sc.Code;
  const titleOf = (sc) => sc.title ?? sc.Title;
  const actionsOf = (sc) => (sc.actions ?? sc.Actions ?? []).map((a) => String(a).toLowerCase());
  const parentOf = (sc) => sc.parentCode ?? sc.ParentCode ?? null;

  // ---- Yükleme (tek efekt: katalog + gruplar + profil) ----
  useEffect(() => {
    (async () => {
      try {
        // Katalog + grup listesi (grup listesi yalnız mevcut chip etiketlerini çözmek için).
        const [catRes, grpRes] = await Promise.all([
          fetch(`${API}/ScreenCatalog`, { headers: authHeaders() }),
          fetch(`${GROUPS_API}/Select`, { headers: authHeaders() }),
        ]);
        const cat = catRes.ok ? (unwrap(await catRes.json()) || []) : [];
        setCatalog(cat);
        setExpanded(new Set(cat.filter((sc) => !parentOf(sc)).map(codeOf)));

        const grpList = grpRes.ok ? (unwrap(await grpRes.json()) || []) : [];
        const labelOf = (gid) => {
          const g = grpList.find((x) => String(pick(x, 'id', 'Id')) === String(gid));
          return g ? relationLabel(g) : String(gid);
        };

        // Yeni kayıt (klon dahil) ise profil çekme yok; klon efekti formu doldurur.
        if (!isNew && !location.state?.clone) {
          const res = await fetch(`${API}/GetById?id=${id}`, { headers: authHeaders() });
          if (!res.ok) throw new Error(`Profil alınamadı (${res.status})`);
          const d = unwrap(await res.json());
          setForm({
            name: d.name ?? d.Name ?? '',
            description: d.description ?? d.Description ?? '',
            isActive: (d.isActive ?? d.IsActive ?? true) ? 'true' : 'false',
          });
          const map = {};
          (d.rights || d.Rights || []).forEach((r) => {
            map[pick(r, 'screenCode', 'ScreenCode')] = {
              canView: !!pick(r, 'canView', 'CanView'),
              canAdd: !!pick(r, 'canAdd', 'CanAdd'),
              canEdit: !!pick(r, 'canEdit', 'CanEdit'),
              canDelete: !!pick(r, 'canDelete', 'CanDelete'),
              canExport: !!pick(r, 'canExport', 'CanExport'),
            };
          });
          setRights(map);
          setGroupChips((d.groupIds || d.GroupIds || []).map((gid) => ({ value: String(gid), label: labelOf(gid) })));
          setAudit({ createdBy: pick(d, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(d, 'createdDate', 'CreatedDate'), updatedBy: pick(d, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(d, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
        }
      } catch (err) {
        showToast('error', err.message || 'Profil alınamadı');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  // Klonla: /mobile-permissions/new'e kopya veriyle gelindiyse formu+izinleri doldur.
  useEffect(() => {
    if (isNew && location.state?.clone) {
      const c = location.state.clone;
      setForm({ name: c.name, description: c.description, isActive: 'true' });
      setRights(c.rights || {});
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // ---- Katalog gruplama ----
  const grouped = useMemo(() => {
    const cats = [];
    const seen = new Map();
    catalog.filter((sc) => !parentOf(sc)).forEach((sc) => {
      const cc = sc.categoryCode ?? sc.CategoryCode ?? 'other';
      if (!seen.has(cc)) { const c = { code: cc, name: sc.categoryName ?? sc.CategoryName ?? 'Diğer', screens: [] }; seen.set(cc, c); cats.push(c); }
      seen.get(cc).screens.push(sc);
    });
    const childrenOf = (code) => catalog.filter((x) => parentOf(x) === code);
    return { cats, childrenOf };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog]);

  const effective = useCallback((code, actions) => rights[code] || openRight(actions), [rights]);

  const matches = useCallback((sc) => {
    const q = search.trim().toLocaleLowerCase('tr');
    if (!q) return true;
    const t = `${titleOf(sc) ?? ''} ${sc.description ?? sc.Description ?? ''}`.toLocaleLowerCase('tr');
    return t.includes(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // ---- İzin değiştirme ----
  const setRight = (code, actions, field, value) => {
    setRights((prev) => {
      const cur = prev[code] || openRight(actions);
      const next = { ...cur, [field]: value };
      if (field === 'canView' && !value) ACTION_DEFS.forEach((a) => { next[a.field] = false; });
      if (field !== 'canView' && value) next.canView = true;
      return { ...prev, [code]: next };
    });
  };
  const applyQuick = (mode) => {
    const map = {};
    catalog.forEach((sc) => {
      const code = codeOf(sc); const acts = actionsOf(sc);
      if (mode === 'full') map[code] = openRight(acts);
      else if (mode === 'readonly') map[code] = { canView: acts.includes('view'), canAdd: false, canEdit: false, canDelete: false, canExport: false };
      else map[code] = { canView: false, canAdd: false, canEdit: false, canDelete: false, canExport: false };
    });
    setRights(map);
  };
  const toggleExpand = (code) => setExpanded((p) => { const n = new Set(p); n.has(code) ? n.delete(code) : n.add(code); return n; });

  // ---- Sayaçlar ----
  const stats = useMemo(() => {
    const tops = catalog.filter((sc) => !parentOf(sc));
    let accessible = 0, active = 0;
    catalog.forEach((sc) => {
      const acts = actionsOf(sc); const eff = effective(codeOf(sc), acts);
      if (!parentOf(sc) && eff.canView) accessible++;
      acts.forEach((a) => { const f = ACTION_DEFS.find((d) => d.key === a); if (f && eff[f.field]) active++; });
    });
    return { total: tops.length, accessible, active };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, rights]);

  // ---- Kaydet ----
  const buildRightsPayload = () => catalog.map((sc) => {
    const code = codeOf(sc); const r = rights[code] || openRight(actionsOf(sc));
    return { screenCode: code, ...r };
  });

  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.name.trim()) { showToast('error', 'Profil adı zorunludur'); setTab('info'); return; }
    setSaving(true);
    try {
      let profileId = isNew ? null : Number(id);
      if (isNew) {
        const addRes = await fetch(`${API}/Add`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ name: form.name.trim(), description: form.description?.trim() }) });
        const addBody = unwrap(await addRes.json().catch(() => ({})));
        if (!addRes.ok) throw new Error(pick(addBody, 'message', 'Message') || 'Kayıt eklenemedi');
        profileId = addBody?.id ?? addBody?.Id;
      } else {
        const upRes = await fetch(`${API}/Update`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: profileId, name: form.name.trim(), description: form.description?.trim(), isActive: form.isActive === 'true' }) });
        if (!upRes.ok) throw new Error('Kayıt güncellenemedi');
      }
      // İzinler + grup atamaları.
      await fetch(`${API}/Rights`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ profileId, rights: buildRightsPayload() }) });
      await fetch(`${API}/AssignGroups`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ profileId, groupIds: groupChips.map((c) => c.value) }) });

      if (isNew) {
        if (andNew) {
          showToast('success', 'Profil eklendi. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM); setRights({}); setGroupChips([]); setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Profil başarıyla eklendi');
        setTimeout(() => navigate('/mobile-permissions'), 400);
        return;
      }
      showToast('success', 'Profil başarıyla kaydedildi');
      setTimeout(() => navigate('/mobile-permissions'), 400);
    } catch (err) {
      showToast('error', err.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleClone = () => navigate('/mobile-permissions/new', { state: { clone: { name: `${form.name} (Kopya)`, description: form.description, rights } } });

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: Number(id) }) });
      if (!res.ok) throw new Error('Kayıt silinemedi');
      showToast('success', 'Profil başarıyla silindi');
      setTimeout(() => navigate('/mobile-permissions'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme işlemi sırasında hata oluştu');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const statusBadge = !isNew && (form.isActive === 'true'
    ? <StatusBadge tone="ok">Aktif</StatusBadge>
    : <StatusBadge tone="neutral">Pasif</StatusBadge>);

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/mobile-permissions')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton label={saving ? 'Kaydediliyor…' : 'Kaydet'} icon="check" onClick={() => persist()} disabled={saving}
          menu={[{ label: 'Kaydet ve Yeni', desc: 'Kaydet, listeye dönme, yeni kayıt aç', icon: 'plus', onClick: () => persist({ andNew: true }) }]} />
      ) : (
        <SplitButton label={saving ? 'Kaydediliyor…' : 'Kaydet'} icon="check" onClick={() => persist()} disabled={saving}
          menu={[{ label: 'Klonla', desc: 'Bu profili izinleriyle kopyala', icon: 'copy', onClick: handleClone }]} />
      ))}
    </>
  );

  // ---- Ekran satırı render ----
  const ST = RIGHTS_STYLES;
  const renderScreen = (sc, isChild) => {
    const code = codeOf(sc); const acts = actionsOf(sc);
    const eff = effective(code, acts);
    const children = grouped.childrenOf(code);
    const hasKids = children.length > 0;
    const isOpen = expanded.has(code);
    return (
      <div key={code}>
        <div style={{ ...ST.row, ...(isChild ? ST.rowChild : {}) }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
            {hasKids ? <button type="button" style={ST.chev} onClick={() => toggleExpand(code)}><Icon name={isOpen ? 'chevD' : 'chevR'} size={14} /></button> : <span style={{ width: 16 }} />}
            <div style={{ minWidth: 0 }}>
              <div style={ST.title}>{titleOf(sc)}{hasKids && <span style={ST.subTag}>{children.length} ALT FONKSİYON</span>}</div>
              <div style={ST.desc}>{sc.description ?? sc.Description}</div>
            </div>
          </div>
          <div style={ST.actions}>
            {ACTION_DEFS.map((a) => acts.includes(a.key) ? (
              <label key={a.key} style={ST.cell}>
                <span style={ST.cellLbl}>{a.label}</span>
                <input type="checkbox" checked={!!eff[a.field]} onChange={(e) => setRight(code, acts, a.field, e.target.checked)} />
              </label>
            ) : <span key={a.key} style={ST.cellEmpty} />)}
          </div>
        </div>
        {hasKids && isOpen && children.filter((c) => matches(c) || matches(sc)).map((c) => renderScreen(c, true))}
      </div>
    );
  };

  const quickApply = (
    <div style={{ display: 'flex', gap: 8 }}>
      <button type="button" className="dps-btn ghost" onClick={() => applyQuick('full')}>Tam Yetki</button>
      <button type="button" className="dps-btn ghost" onClick={() => applyQuick('readonly')}>Salt Okunur</button>
      <button type="button" className="dps-btn ghost" onClick={() => applyQuick('none')}>Tümünü Kapat</button>
    </div>
  );

  return (
    <DetailShell
      crumb={[{ label: 'Mobil Yetkilendirme', to: '/mobile-permissions' }, { label: isNew ? 'Yeni Profil' : 'Profil Detayı', current: true }]}
      status={statusBadge}
      tabs={[
        { key: 'info', label: 'Profil Bilgileri', icon: 'shield' },
        { key: 'rights', label: 'Ekran Yetkileri', icon: 'list' },
        { key: 'groups', label: 'Temsilci Grupları', icon: 'users' },
      ]}
      activeTab={tab}
      onTab={setTab}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'PermissionProfile', entityId: id }}
      loading={loading}
    >
      {tab === 'info' && (
        <div className="dps-form">
          <Section icon="shield" title="Profil Bilgileri" subtitle="Profil adı, açıklama ve durum">
            <Grid cols={2}>
              <FieldText form={form} set={set} tried={tried} k="name" label="Profil Adı" req />
              <FieldSelect form={form} set={set} k="isActive" label="Durum" options={ACTIVE_OPTIONS} />
              <FieldTextarea form={form} set={set} k="description" label="Açıklama" span />
            </Grid>
          </Section>
        </div>
      )}

      {tab === 'rights' && (
        <div className="dps-form">
          <Section
            icon="list"
            title="Ekran Yetkileri"
            subtitle={`${stats.accessible} / ${stats.total} ekrana erişim · ${stats.active} aktif izin`}
            right={quickApply}
            note="İzin satırı eklenmeyen ekran açık varsayılır (geriye dönük uyum). Bir ekranı tamamen kapatmak için Görüntüle'yi kaldırın."
          >
            <input style={RIGHTS_STYLES.search} placeholder="🔍  Ekran veya fonksiyon ara…" value={search} onChange={(e) => setSearch(e.target.value)} />
            {grouped.cats.map((cat) => {
              const vis = cat.screens.filter((sc) => matches(sc) || grouped.childrenOf(codeOf(sc)).some(matches));
              if (!vis.length) return null;
              return (
                <div key={cat.code} style={{ marginBottom: 14 }}>
                  <div style={RIGHTS_STYLES.catHead}><span>{cat.name}</span><span style={RIGHTS_STYLES.catCount}>{cat.screens.length}</span></div>
                  {vis.map((sc) => renderScreen(sc, false))}
                </div>
              );
            })}
          </Section>
        </div>
      )}

      {tab === 'groups' && (
        <div className="dps-form">
          <Section
            icon="users"
            title={`Temsilci Grupları (${groupChips.length})`}
            subtitle="Bu profili kullanacak temsilci gruplarını ara ve ekle — seçtiğin gruptaki mobil kullanıcılar bu profilin izinlerini alır."
          >
            <FieldCustom label="Temsilci grubu ekle (kod/açıklama ile ara, çoklu seçim)">
              <MultiChipSelect
                items={groupChips}
                onChange={setGroupChips}
                config={RELATION_CONFIGS.representativeGroup}
                placeholder="Temsilci grubu ara ve ekle…"
              />
            </FieldCustom>
          </Section>
        </div>
      )}

      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}><strong>“{form.name}”</strong> profilini silmek istediğinize emin misiniz?</p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

const RIGHTS_STYLES = {
  search: { margin: '0 0 14px', padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-card)', fontSize: 14, width: '100%', boxSizing: 'border-box', color: 'var(--text-primary)' },
  catHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 4px', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' },
  catCount: { fontSize: 12, color: 'var(--text-secondary)', background: 'var(--surface-app)', borderRadius: 999, padding: '1px 8px' },
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '12px 14px', borderRadius: 12, background: 'var(--bg-card)', border: '1px solid var(--border-color)', marginBottom: 6 },
  rowChild: { marginLeft: 26, background: 'var(--surface-card)' },
  chev: { border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-secondary)', width: 16, padding: 0, display: 'inline-flex' },
  title: { fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 },
  subTag: { fontSize: 10, fontWeight: 700, color: 'var(--accent)', background: 'var(--accent-soft)', borderRadius: 4, padding: '1px 6px', letterSpacing: 0.5 },
  desc: { fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 },
  actions: { display: 'flex', gap: 4, flexShrink: 0 },
  cell: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, width: 64, cursor: 'pointer' },
  cellEmpty: { width: 64 },
  cellLbl: { fontSize: 10, color: 'var(--text-secondary)' },
  groupRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', cursor: 'pointer', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: 10, background: 'var(--bg-card)' },
};

export default MobilePermissionDetailPage;
